// @ts-check
import SITE_LANGUAGE from "./siteLanguage";
import { singleFundIndex } from "./ais/client";
import { translate } from "./translate";

/**
 * @typedef FundDetails
 * @property {string} category
 * @property {string} assetClass - e.g. Equity
 * @property {CalendarReturn[]} calendarReturns - Sorted by ascending year.
 * @property {string} commentary
 * @property {Number|null} distributionAmount
 * @property {string|null} distributionDate - ISO 8601 formatted.
 * @property {string|null} distributionFrequency - e.g. Annually
 * @property {string|null} distributionFrequencyDisplay - e.g. Translated
 * @property {Number|null} distributionReinvestmentPrice - Dollar amount.
 * @property {Number|null} projectedYield - Number to be turned into percentage.
 * @property {Number|null} feeMer - Percentage.
 * @property {string|null} growthChartSmall - String URL.
 * @property {string|null} growthChartLarge - String URL.
 * @property {string} inceptionDate - ISO 8601 formatted.
 * @property {Number} nav - Dollar amount.
 * @property {string} navDate - ISO 8601 formatted.
 * @property {string} pdfProfileUrl
 * @property {Number|null} performance10year - Percentage.
 * @property {Number|null} performance1day - Percentage.
 * @property {Number|null} performance1month - Percentage.
 * @property {Number|null} performance1year - Percentage.
 * @property {Number|null} performance2year - Percentage.
 * @property {Number|null} performance3month - Percentage.
 * @property {Number|null} performance3year - Percentage.
 * @property {Number|null} performance5year - Percentage.
 * @property {Number|null} performance6month - Percentage.
 * @property {string|null} performanceDate - ISO 8601 formatted.
 * @property {Number|null} performanceInception - Percentage.
 * @property {Number|null} performanceYtd - Percentage.
 * @property {Number|null} portfolioAverageDuration - Number of years.
 * @property {string|null} portfolioManager
 * @property {Number|null} portfolioYield - Percentage.
 * @property {string} priceHistoryUrl
 * @property {PurchaseOption[]} purchaseOptions
 * @property {string|null} riskRating - e.g. Medium.
 * @property {string} refinitivId - e.g. 68765904.
 * @property {string} fundName - Name of fund with purchase option and/or series.
 * @property {Number|null} stdDev3year - Percentage.
 * @property {Number|null} stdDev5year - Percentage.
 * @property {Number|null} stdDev10year - Percentage.
 * @property {string|null} stdDevDate - ISO 8601.
 * @property {TopHolding[]} topHoldings
 * @property {string|null} topHoldingsDate - ISO 8601 formatted date top holdings data was updated.
 * @property {string} whyInvest - Text.
 * @property {object|null} yield - Current and effective yield objects.
 */

/**
 * @typedef PurchaseOption
 * @property {string} hedged
 * @property {string} baseCurrency
 * @property {string} fundservCode
 * @property {string} series
 * @property {string} purchaseOption
 */

/**
 * @typedef CalendarReturn
 * @property {string} end - ISO 8601 formatted.
 * @property {Number} value - Percentage.
 * @property {string} start - ISO 8601 formatted.
 */

/**
 * @typedef TopHolding
 * @property {string} name
 * @property {Number} percentage
 */

/**
 * @type FundDetails
 */
export const nullFundDetails = {
	assetClass: "",
	category: "",
	calendarReturns: [],
	commentary: "",
	distributionAmount: 0,
	distributionDate: "",
	distributionFrequency: "",
	distributionFrequencyDisplay: "",
	distributionReinvestmentPrice: 0,
	projectedYield: null,
	feeMer: 0,
	growthChartSmall: null,
	growthChartLarge: null,
	inceptionDate: "",
	nav: 0,
	navDate: "",
	pdfProfileUrl: "",
	performance10year: 0,
	performance1day: 0,
	performance1month: 0,
	performance1year: 0,
	performance2year: 0,
	performance3month: 0,
	performance3year: 0,
	performance5year: 0,
	performance6month: 0,
	performanceDate: "",
	performanceInception: 0,
	performanceYtd: 0,
	portfolioAverageDuration: null,
	portfolioManager: "",
	portfolioYield: 0,
	priceHistoryUrl: "",
	purchaseOptions: [],
	riskRating: "",
	refinitivId: "",
	fundName: "",
	stdDev3year: 0,
	stdDev5year: 0,
	stdDev10year: 0,
	stdDevDate: "",
	topHoldings: [],
	topHoldingsDate: "",
	whyInvest: "",
	yield: null
};

/**
 * @param {string} refinitivId - e.g. 00000000.
 * @returns {Promise<FundDetails>}
 */
export const getFundDetailsByCode = async (refinitivId) => {
	/**
	 * @param {string} refinitivId
	 * @return {Promise<Object>}
	 */
	const fetchResponse = async (refinitivId) => {

		const response = await singleFundIndex.getObject(refinitivId);

		if (response.status < 200 || response.status > 299) {
			throw new Error("Failed to fetch fund details.");
		}

		if (response.status === 404) {
			throw new Error("Object ID not found in index.");
		}

		return response;
	};

	/**
	 * @param {object} response
	 * @returns FundDetails
	 */
	const hydrateFundDetails = (response) => {
		/**
		 * If the commentary text defers to another site, we update the copy.
		 *
		 * @param {string} text
		 * @return string
		 */
		const parseCommentary = (text) => {
			if (
				text.match(
					/(For the Fund commentary, please click here|Pour consulter le commentaire sur le Fonds, veuillez cliquer ici)/
				)?.length
			) {
				const URL_FILTER =
					/((\w+:\/\/)[-a-zA-Z0-9:@;?&=\/%\+\.\*!'\(\),\$_\{\}\^~\[\]`#|]+)/;

				const url = (text.match(URL_FILTER) ?? [])[0];

				if (url) {
					return window.PM_COMMENTARY_READ_MORE.replace("{url}", url);
				}
			}

			try {
				const url = new URL(text);
				return window.PM_COMMENTARY_READ_MORE.replace("{url}", text);
			} catch (_error) {
				// Not a URL; do nothing
			}

			return text;
		};

		return {
			assetClass: response.assetClass,
			category: response.category,
			calendarReturns: response.annualPerformanceData ? response.annualPerformanceData.data
				.filter((calendarReturn) => calendarReturn !== null)
				.sort((a, b) => b.start < a.end) : [],
			commentary: response.commentary ? parseCommentary(response.commentary) : null,
			distributionAmount: response?.distribution?.amount ? response.distribution.amount : null,
			distributionDate: response?.distribution?.date ? response.distribution.date : null,
			distributionFrequency: response?.distribution?.frequency ? response.distribution.frequency : null,
			distributionFrequencyDisplay: response?.distribution?.frequency
				? translate(response.distribution.frequency)
				: null,
			distributionReinvestmentPrice: response?.distribution?.reinvestmentPrice
				? response.distribution.reinvestmentPrice
				: null,
			projectedYield: response.projectedYield ? response.projectedYield : null,
			feeMer: response.mer ? response.mer : null,
			growthChartSmall: response.mfundid
				? `https://bmomf.lipperweb.com/bmomf/Chart/Growth10K?width=500&height=500&symbol=45121:${response.mfundid}&lang=${SITE_LANGUAGE}&period=INC`
				: null,
			growthChartLarge: response.mfundid
				? `https://bmomf.lipperweb.com/bmomf/Chart/Growth10K?width=1000&height=400&symbol=45121:${response.mfundid}&lang=${SITE_LANGUAGE}&period=INC`
				: null,
			inceptionDate: response.launchDate,
			nav: response.nav.value ? response.nav.value : null,
			navDate: response.nav.date ? response.nav.date : null,
			pdfProfileUrl: `https://bmomf.lipperweb.com/bmomf/Pdf/?symbol=${response.objectID}&lang=${SITE_LANGUAGE}`,
			performance5year: response.performance['5year'] ? response.performance['5year'] : null,
			performance10year: response.performance['10year'] ? response.performance['10year'] : null,
			performance3month: response.performance['3month'] ? response.performance['3month'] : null,
			performanceYtd: response.performance.ytd ? response.performance.ytd : null,
			performance1day: response.performance['1day'] ? response.performance['1day'] : null,
			performance3year: response.performance['3year'] ? response.performance['3year'] : null,
			performanceDate: response.performance.date ? response.performance.date : null,
			performanceInception: response.performance.inception ? response.performance.inception : null,
			performance6month: response.performance['6month'] ? response.performance['6month'] : null,
			performance2year: response.performance['2year'] ? response.performance['2year'] : null,
			performance1year: response.performance['1year'] ? response.performance['1year'] : null,
			performance1month: response.performance['1month'] ? response.performance['1month'] : null,
			portfolioAverageDuration: response?.portfolioStats?.duration ? response.portfolioStats.duration : null,
			portfolioManager: response.portfolioManager ? response.portfolioManager : null,
			portfolioYield: response?.portfolioStats?.yield ? response.portfolioStats.yield : null,
			priceHistoryUrl: `https://bmomf.lipperweb.com/bmomf/profile/?symbol=45121:${response.mfundid}&lang=${SITE_LANGUAGE}#History`,
			purchaseOptions: response.purchaseOptions.filter(
				(option) => option !== null
			).map(option => {
				// When the option is not provided, it is FE.
				if (!option.purchaseOption) {
					option.purchaseOption = "FE";
				}
				return option
			}),
			riskRating: response.riskRating ? response.riskRating : null,
			fundName: response.legalName,
			stdDev3year: response?.standardDeviation?.['3year'] ? response.standardDeviation['3year'] : null,
			stdDev5year: response?.standardDeviation?.['5year'] ? response.standardDeviation['5year'] : null,
			stdDev10year: response?.standardDeviation?.['10year'] ? response.standardDeviation['10year'] : null,
			stdDevDate: response?.standardDeviation?.date ? response.standardDeviation.date : null,
			topHoldings:
				response.topHoldings &&
				response.topHoldings.entries instanceof Array &&
				response.topHoldings.entries.filter((holding) => holding).length > 0
					? response.topHoldings.entries
							.filter((holding) => holding)
							.map((topHolding) => {
								topHolding.name = topHolding.name.replace(/ORD/, "").trim();
								return topHolding;
							})
					: [],
			topHoldingsDate: response?.topHoldings?.date ? response.topHoldings.date : null,
			whyInvest: response.reasonsToInvest || [],
			yield: response.yield7day ? response.yield7day : null
		};
	};

	let refinitivResponse;
	window.chartData = {};

	try {
		refinitivResponse = await fetchResponse(refinitivId);
	} catch (error) {
		console.error(error);
		window.location.assign("/404");
	}

	// make these readily accessible to our charts and alpine
	window.chartData = {
		annualPerformanceData: refinitivResponse.annualPerformanceData ? refinitivResponse.annualPerformanceData.data : null,
		assetAllocation: refinitivResponse.assetAllocation ? refinitivResponse.assetAllocation : null,
		geographicAllocation: refinitivResponse.geographicAllocation ? refinitivResponse.geographicAllocation : null,
		sectorAllocation: refinitivResponse.sectorAllocation ? refinitivResponse.sectorAllocation : null,
	}

	const response = hydrateFundDetails(refinitivResponse);
	document.title += ` | ${response.fundName}`;

	return response;
};
